import * as Yup from "yup";
import moment from 'moment'
import Age from '../Dates/Age';
import AddAdditionalState from '../Lists/AddAdditionalState';
import creditCardType, {types as CardType} from "credit-card-type";

const { object, string, ref, boolean, array, number, date, mixed } = Yup

Yup.addMethod(Yup.string, 'isValidNumbersOnly', (message, charLimit) => {

  return Yup.string().test(`isValidNumbersOnly`, message, function (value) {
    const { path, createError } = this;

    let regex;
    if (charLimit) {
      regex = RegExp(`^[0-9]{${charLimit}}$`);
    }
    else {
      regex = RegExp(/^[0-9]*$/);
    }

    return regex.test(value) || createError({ path, message });
  });
})

Yup.addMethod(Yup.string, 'isValidPhone', (message, charLimit) => {

  return Yup.string().test(`isValidPhone`, message, function (value) {
    const { path, createError } = this;

    const regex = RegExp(/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/);

    return regex.test(value) || createError({ path, message });
  });
})

Yup.addMethod(Yup.string, 'isValidSSN', (message, charLimit) => {

  return Yup.string().test(`isValidSSN`, message, function (value) {
    const { path, createError } = this;

    const regex = RegExp(/(^\d{3}-?\d{2}-?\d{4}$)/);

    return regex.test(value) || createError({ path, message });
  });
})

Yup.addMethod(Yup.string, 'isValidHeightInchString', (message, minInch, maxInch) => {

  return Yup.string().test(`isValidHeightInchString`, message, function (value) {
    const { path, createError } = this;
    const regex = /(\d+)\s*ft\s*(\d*)\s*in*/;
    const heightFt = value?.charAt(0).trim();
    const heightIn = value?.substring(5, 7).trim();

    if (!value || (value && !regex.test(value)))
      return createError({ path, message: 'Required'})

    if (!heightIn) {
      return createError({ path, message: 'Must provide height in inch.' });
    }

    if (heightIn > 11) {
      return createError({ path, message: 'Maximum of 11 inches.' });
    }

    if (!heightFt) {
      return createError({ path, message: 'Must provide height in feet.' });
    }

    if (heightFt && Number(heightFt) === 0 && heightIn < 1) {
      return createError({ path, message: 'Minimum of 1 inch.' });
    }

    const totalInches = parseInt(heightIn, 10) + parseInt((heightFt * 12), 10);

    if (totalInches < minInch) {
      return createError({ path, message: `Height should be more than ${minInch} inches.` });
    }

    if (totalInches > maxInch) {
      return createError({ path, message: `Height should be less than ${maxInch} inches.` });
    }

    return true;
  });
})

Yup.addMethod(Yup.string, 'isValidVisaNumber', (message, charLimit) => {

  return Yup.string().test(`isValidVisaNumber`, message, function (value) {
    const { path, createError } = this;

    const regex = RegExp(/[a-zA-Z]{3}\d{10}$/);

    return regex.test(value) || createError({ path, message });
  });
})

Yup.addMethod(Yup.string, 'isValidBirthdate', (message, minAge, maxAge) => {

  return Yup.string().test(`isValidBirthdate`, message, function (value) {
    const { path, createError } = this;

    value = moment(new Date(value));

    const age = Age(value);
    const currentDate = moment(new Date());

    let isWithinAgeRange = true

    if (maxAge) {
      if (age > maxAge) {
        isWithinAgeRange = false;
        message = 'You are over the maximum age.';
      }
    }

    if (minAge) {
      if (age < minAge) {
        isWithinAgeRange = false;
        message = 'You are under the minimum age.';
      }
    }

    if (value > currentDate) {
      isWithinAgeRange = false;
      message = 'Cannot be in the future.'
    }

    return (value.isValid() && isWithinAgeRange) || createError({ path, message });
  })
})

Yup.addMethod(Yup.string, 'isValidNoQuotes', (message) => {

  return Yup.string().test(`isValidNoQuotes`, message, function (value) {
    const { path, createError } = this;

    const regex = RegExp(/^[^'"]*$/);

    return regex.test(value) || createError({ path, message });
  });
})

Yup.addMethod(Yup.string, 'isValidAlphanumeric', (message, charLimit) => {

  return Yup.string().test(`isValidAlphanumeric`, message, function (value) {
    const { path, createError } = this;

    let regex;
    if (charLimit) {
      regex = RegExp(`^[a-zA-Z0-9]{${charLimit}}$`);
    }
    else {
      regex = RegExp(/^[a-zA-Z0-9]*$/);
    }

    return regex.test(value) || createError({ path, message });
  });
})

Yup.addMethod(Yup.string, 'isValidAddress', (message) => {

  return Yup.string().test(`isValidAddress`, message, function (value) {
    const { path, createError } = this;

    const regex = RegExp(/^[a-zA-Z0-9 ]*$/);

    return regex.test(value) || createError({ path, message });
  });
})

Yup.addMethod(Yup.string, 'isValidState', (message, basicInfoEligibleState) => {
  return Yup.string().test(`isValidState`, message, function (value) {
    const { path, createError } = this;
    let isValid = true;

    var states = AddAdditionalState([{ label: "Guam", value: "GU" }]);
    var eligibleState = basicInfoEligibleState.includes(value);
    const stateName = states.find(x => x.value === value);

    if (eligibleState === true) {
      isValid = false;
      message = `Product not available in state of ${stateName.label}`;
    }

    return isValid || createError({ path, message });
  })
})

Yup.addMethod(Yup.string, 'isValidNameLimitedSpecialChar', (message, isRequired, address) => {

  return Yup.string().test(`isValidNameLimitedSpecialChar`, message, function (value) {
    const { path, createError } = this;
    const firstChar = value?.slice(0, 1);
    const lastChar = value?.slice(-1);

    const firstAndLastCharRegex = RegExp(/^[a-zA-Z]$/);
    const fullNameRegex = RegExp(/^(?!.*?[ '*-]{2})[A-Za-z., '*-]{1,35}$/);

    const isValid = firstAndLastCharRegex.test(firstChar) &&
      firstAndLastCharRegex.test(lastChar) &&
      fullNameRegex.test(value) && value.split(',').length <= 2 && value.split('.').length <= 2;

    const numbers = RegExp(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/);
    const isValidNUmbers = numbers.test(value);

    if (isRequired === false) {
      if (value !== "" && isValidNUmbers === false && address === false) {
        message = 'Numbers and special characters are not allowed.';
      }
      else if (value !== "" && isValidNUmbers === false && address === true) {
        return true;
      }
      else if (value === "" || value === undefined) {
        return true;
      }
      else if (!isValid) {
        message = "Invalid data. Remove extra characters or spaces.";
      }
    }

    if (isRequired === true) {
      if (value === "" || value === undefined) {
        message = this.message;
      }
      else if (value !== "" && !isValid && isValidNUmbers === false && address === true) {
        return true;
      }
      else if (value !== "" && !isValid && isValidNUmbers === false) {
        message = "Numbers and special characters are not allowed.";
      }
      else if (value !== "" && !isValid) {
        message = "Invalid data. Remove extra characters or spaces.";
      }
    }

    return isValid || createError({ path, message });
  });
})

Yup.addMethod(Yup.string, 'isValidNameWithFiftyChar', (message, isRequired) => {

  return Yup.string().test(`isValidNameWithFiftyChar`, (message), function (value) {
    const { path, createError } = this;

    const firstChar = value?.slice(0, 1);
    const lastChar = value?.slice(-1);

    const firstAndLastCharRegex = RegExp(/^[a-zA-Z]$/);
    const fullNameRegex = RegExp(/^(?!.*?[ '*-]{2})[A-Za-z., '*-]{1,50}$/);

    const isValid = firstAndLastCharRegex.test(firstChar) &&
      firstAndLastCharRegex.test(lastChar) &&
      fullNameRegex.test(value) && value.split(',').length <= 2 && value.split('.').length <= 2;

    const numbers = RegExp(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/);
    const isValidNUmbers = numbers.test(value);

    if (isRequired === true) {
      if (!value || value === "") {
        message = this.message;
      }
      else if (value !== '' && isValid && isValidNUmbers === false) {
        return true;
      }
      else if (value !== "" && !isValid && isValidNUmbers === false) {
        message = "Numbers and special characters are not allowed.";
      }
      else if (value !== "" && !isValid) {
        message = "Invalid data. Remove extra characters or spaces.";
      }
    }
    if (isRequired === false) {
      if (value === "") {
        return true;
      }
      else if (value !== "" && isValidNUmbers === false) {
        message = 'Numbers and special characters are not allowed.';
      }
      else if (!isValid) {
        message = "Invalid data. Remove extra characters or spaces.";
      }
    }

    return isValid || createError({ path, message });
  });
})

Yup.addMethod(Yup.string, 'isValidHeightString', (message, minFt, maxFt = 7) => {

  return Yup.string().test(`isValidHeightString`, message, function (value) {
    const { path, createError } = this;

    let isValid = true;

    const heightFt = value.charAt(0);
    const heightIn = value.substring(5, 7);

    if (heightFt > maxFt) {
      isValid = false;
      message = `Maximum of ${maxFt} feet.`
    }

    if (heightFt < minFt) {
      isValid = false;
      message = `Minimum of ${minFt} feet.`
    }

    if (heightIn > 11) {
      isValid = false;
      message = 'Maximum of 11 inches.'
    }

    if (!heightFt || heightFt === ' ') {
      isValid = false;
      message = 'Must provide height in feet.'
    }

    if ((!heightIn || heightIn === '  ') && isValid) {
      isValid = false;
      message = 'Must provide height in inches.'
    }

    return isValid || createError({ path, message });
  })
})

Yup.addMethod(Yup.string, 'isValidWeightString', (message, minWeight, maxWeight, requiredMessage = null) => {

  return Yup.string().test(`isValidWeightString`, message, function (value) {
    const { path, createError } = this;
    
    if (!value || value === '' || value.trim() === 'pounds' || value.trim() === 'lbs') {
      return createError({ path, message: requiredMessage ? requiredMessage : 'Weight is required.'});
    }

    const weightValue = (value.trim().split(' ')[0]).trim();
    if (weightValue && weightValue < 1) {
      return createError({ path, message: 'Invalid Weight'});
    }

    if (weightValue > maxWeight) {
      return createError({ path, message: `Weight should be less than ${maxWeight} pounds.` });
    }

    if (weightValue < minWeight) {
      return createError({ path, message: `Weight should be more than ${minWeight} pounds.` });
    }

    return true;
  })
})

Yup.addMethod(Yup.string, 'isValidAmericoHeightString', (message, minInch = 58, maxInch = 80) => {
  return Yup.string().test(`isValidAmericoHeightString`, message, function (value) {
    const { path, createError } = this;

    let isValid = true;

    const heightFt = value.charAt(0);
    const heightIn = value.substring(5, 7);
    const totalHeightInches = parseInt(heightFt, 10) * 12 + parseInt(heightIn, 10);

    if (totalHeightInches > maxInch) {
      isValid = false;
      message = `Height should be less than ${maxInch} inches.`
    }

    if (totalHeightInches < minInch) {
      isValid = false;
      message = `Height should be more than ${minInch} inches.`
    }

    if (heightIn > 11) {
      isValid = false;
      message = 'Maximum of 11 inches.'
    }

    if (!heightFt || heightFt === ' ') {
      isValid = false;
      message = 'Must provide height in feet.'
    }

    if ((!heightIn || heightIn === '  ') && isValid) {
      isValid = false;
      message = 'Must provide height in inches.'
    }

    return isValid || createError({ path, message });
  })
})

Yup.addMethod(Yup.string, 'isValidEtradeHeightString', (message, minInch, maxInch) => {

  return Yup.string().test(`isValidEtradeHeightString`, message, function (value) {
    const { path, createError } = this;

    let isValid = true;

    const heightFt = value?.charAt(0);
    const heightIn = value?.substring(5, 7);

    if (heightIn > 11) {
      isValid = false;
      message = 'Maximum of 11 inches.'
    }

    if (!heightFt || heightFt === ' ') {
      isValid = false;
      message = 'Must provide height in feet.';
    }

    if ((!heightIn || heightIn === '  ') && isValid) {
      isValid = false;
      message = 'Must provide height in inches.';
    }
    const totalInches = parseInt(heightIn, 10) + parseInt((heightFt * 12), 10);

    if (isValid && totalInches < minInch) {
      isValid = false;
      message = `Minimum of ${(minInch / 12)} feet.`;
    }

    if (isValid && totalInches > maxInch) {
      isValid = false;
      message = `Maximum of ${(maxInch / 12)} feet.`;
    }

    return isValid || createError({ path, message });
  })
})

Yup.addMethod(Yup.string, 'isUnder12Months', (message, currentBirthdate) => {

  return Yup.string().test(`isUnder12Months`, message, function (value) {
    const { path, createError } = this;
    let isValidDifference = true;

    const currentDate = moment(new Date())
    const date = moment(new Date(currentBirthdate))
    var isUnderLimit = currentDate.diff(date, 'months') < 12

    if (value === 'true' && !isUnderLimit) {
      isValidDifference = false
      message = "Child's age is greater than 12 months"
    } else if (value === 'false' && isUnderLimit) {
      isValidDifference = false
      message = "Child's age is not greater than 12 months"
    }

    return isValidDifference || createError({ path, message });
  })
})

Yup.addMethod(Yup.string, 'isJuniorAge13orOver', (message, currentAge) => {

  return Yup.string().test(`isJuniorAge13orOver`, message, function (value) {
    const { path, createError } = this;

    let isValidAgeDifference = true;
    var isUnderLimit = currentAge < 13

    if (value === 'true' && isUnderLimit) {
      isValidAgeDifference = false
      message = "Child's age is not greater than 13."
    } else if (value === 'false' && !isUnderLimit) {
      isValidAgeDifference = false
      message = "Child's age is greater than 13."

    }

    return isValidAgeDifference || createError({ path, message });
  })
})

Yup.addMethod(Yup.string, 'isValidSpecificStartDate', (message, piBirthdate) => {

  return Yup.string().test(`isValidSpecificStartDate`, message, function (value) {
    const { path, createError } = this;

    let validDate = true;

    const selectedDate = moment(new Date(value));
    const date = selectedDate.date();

    const currentDate = moment().startOf('day');
    const thirtyDaysOutDate = moment(currentDate).add(30, 'd');

    const nextBirthday = moment(new Date(piBirthdate));
    nextBirthday.year(currentDate.year());
    if (currentDate > nextBirthday) {
      nextBirthday.year(currentDate.year() + 1);
    }

    //moment converting 31st sep 2020 to 1 oct 2020, so  for this type of case use indexof
    if (!selectedDate.isValid()) {
      message = "Invalid date";
    }
    else if (CompareSpecificStartDate(selectedDate, currentDate)) {
      validDate = false;
      message = 'Start date cannot be in past.';
    }
    else if (selectedDate > thirtyDaysOutDate) {
      validDate = false;
      message = 'Start date must be within 30 days and cannot be on the 29th, 30th, or 31st of the month.';
    }
    else if (nextBirthday.isBetween(currentDate, thirtyDaysOutDate) && selectedDate > nextBirthday) {
      validDate = false;
      message = 'Your payment date must occur before your next birthday.';
    }
    else if (date === 29 || date === 30 || date === 31 || value.indexOf('/31/') > -1) {
      validDate = false;
    }

    return (selectedDate.isValid() && validDate) || createError({ path, message });
  })
})

Yup.addMethod(Yup.string, 'isValidCreditCardType', (message, cardNumber) => {
  return Yup.string().test(`isValidCreditCardType`, message, function (value) {
    const { path, createError } = this;
    const VISA_CARD_NUMBER_LENGHTS = [13,16]
    let isValid = false;
    const isOnlyDigits = RegExp(/^[0-9]*$/).test(value);

    message = "Aflac will only accept Visa, Mastercard and American Express. Please submit using a valid card type.";
    if(value === ""){
      isValid = false;
      message = "Card Number is required.";
    }
    else if(!isOnlyDigits){
      isValid = false;
      message = "The credit card number must only contain digits.";
    }
    else{
      creditCardType(value).filter((card) => {
        if(card.type === CardType.MASTERCARD && card.lengths.includes(value.length) && !value.startsWith("2")){
          isValid = true;
        }
        else if(card.type === CardType.VISA && VISA_CARD_NUMBER_LENGHTS.includes(value.length)){
          isValid = true;
        }
        else if(card.type === CardType.AMERICAN_EXPRESS && card.lengths.includes(value.length)){
          isValid = true;
        }
        else if(value.length === 16 && value.startsWith("5")){
          isValid = true;
        }
        else{
          isValid = false;
        }
      });
    }

    return isValid || createError({ path, message });
  });
})

const CompareSpecificStartDate = (startDate, currentDate) => {
  //Allow 28th date  
  const unavailableDraftDates = [29, 30, 31];
  const isSameMonth = startDate.month() === currentDate.month();
  const isSameYear = startDate.year() === currentDate.year();
  if (startDate < currentDate && startDate.date() === 28 && unavailableDraftDates.includes(currentDate.date()) && isSameMonth && isSameYear) {
    return false;
  } else if (startDate < currentDate) {
    return true;
  }
}

Yup.addMethod(Yup.string, 'ValidateDriversLicense', (message, dlState) => {

  return Yup.string().test(`ValidateDriversLicense`, message, function (value) {
    const { path, createError } = this;
    let regex;
    let isValid = true;

    switch (dlState) {
      case "AL":
      case "AK":
      case "DE":
        regex = RegExp(/^\d{1,7}$/);
        break;
      case "AZ":
        regex = RegExp(/^([A-Z]\d{1,8}|[A-Z]{2}\d{2,6}|\d{9})$/);
        break;
      case "AR":
        regex = RegExp(/^\d{4,9}$/);
        break;
      case "CA":
      case "NE":
        regex = RegExp(/^[A-Z]\d{7}$/);
        break;
      case "CO":
        regex = RegExp(/^(\d{9}|[A-Z]\d{3,6}|[A-Z]{2}\d{2,5})$/);
        break;
      case "CT":
      case "MS":
        regex = RegExp(/^\d{9}$/);
        break;
      case "DC":
        regex = RegExp(/^(\d{7}|\d{9})$/);
        break;
      case "FL":
      case "MD":
      case "MN":
        regex = RegExp(/^[A-Z]\d{12}$/);
        break;
      case "GA":
      case "TN":
        regex = RegExp(/^\d{7,9}$/);
        break;
      case "HI":
        regex = RegExp(/^(\d{9}|[A-Z]\d{8})$/);
        break;
      case "ID":
        regex = RegExp(/^([A-Z]{2}\d{6}[A-Z]|\d{9})$/);
        break;
      case "IL":
        regex = RegExp(/^([A-Z]\d{11,12})$/);
        break;
      case "IN":
        regex = RegExp(/^([A-Z]{1}\d{9}|\d{9,10})$/);
        break;
      case "IA":
        regex = RegExp(/^(\d{3}[A-Z]{2}\d{4}|\d{9})$/);
        break;
      case "KS":
        regex = RegExp(/^(\d{9}|[A-Z]\d{8}|[A-Z]\d{1}[A-Z]\d{1}[A-Z])$/);
        break;
      case "KY":
        regex = RegExp(/^(\d{9}|[A-Z]\d{8,9})$/);
        break;
      case "LA":
      case "OR":
        regex = RegExp(/^\d{1,9}$/);
        break;
      case "ME":
        regex = RegExp(/^(\d{7,8}|\d{7}[A-Z])$/);
        break;
      case "MA":
        regex = RegExp(/^([A-Z]\d{8}|\d{9})$/);
        break;
      case "MI":
        regex = RegExp(/^([A-Z]\d{10}|[A-Z]\d{12})$/);
        break;
      case "MO":
        regex = RegExp(/^(\d{9}|\d{9}[A-Z]{1}|\d{8}[A-Z]{2}|[A-Z]{1}\d{5,9}|[A-Z]{1}\d{6}[R])$/);
        break;
      case "MT":
        regex = RegExp(/^(\d{9}|[A-Z]\d[\dA-Z]\d{2}[A-Z]{2}\d|\d{13}|\d{14}|[A-Z]\d{8})$/);
        break;
      case "NV":
        regex = RegExp(/^(\d{9}|\d{10}|\d{12}|[X]\d{8})$/);
        break;
      case "NH":
        regex = RegExp(/^\d{2}[A-Z]{3}\d{5}$/);
        break;
      case "NJ":
        regex = RegExp(/^[A-Z]\d{14}$/);
        break;
      case "NM":
        regex = RegExp(/^\d{8,9}$/);
        break;
      case "NY":
        regex = RegExp(/(^[A-Z]{1}[0-9]{7}$)|(^[A-Z]{1}[0-9]{18}$)|(^[0-9]{8}$)|(^[0-9]{9}$)|(^[0-9]{16}$)|(^[A-Z]{8}$)/);
        break;
      case "NC":
        regex = RegExp(/^\d{1,12}$/);
        break;
      case "ND":
        regex = RegExp(/^(\d{9}|[A-Z]{3}\d{6})$/);
        break;
      case "OH":
        regex = RegExp(/^([A-Z]{1}\d{4,8}|[A-Z]{2}\d{3,7}|\d{8})$/);
        break;
      case "OK":
        regex = RegExp(/^(\d{9}|[A-Z]{1}\d{9})$/);
        break;
      case "PA":
        regex = RegExp(/^\d{8}$/);
        break;
      case "RI":
        regex = RegExp(/^(\d{7}|[A-Z]{1}\d{6})$/);
        break;
      case "SC":
        regex = RegExp(/^\d{5,11}$/);
        break;
      case "SD":
        regex = RegExp(/(^[0-9]{6,10}$)|(^[0-9]{12}$)/);
        break;
      case "TX":
        regex = RegExp(/^\d{7,8}$/);
        break;
      case "UT":
        regex = RegExp(/^\d{4,10}$/);
        break;
      case "VT":
        regex = RegExp(/^\d{7}[\dA]$/);
        break;
      case "VA":
        regex = RegExp(/^(\d{9}|[A-Z]\d{8,11})$/);
        break;
      case "WA":
        regex = RegExp(/^(?=.{12}$)[A-Z]{1,7}[A-Z0-9\\*]{4,11}$/);
        break;
      case "WV":
        regex = RegExp(/^(\d{7}|[A-Z]{1,2}\d{5,6})$/);
        break;
      case "WI":
        regex = RegExp(/^[A-Z]\d{13}$/);
        break;
      case "WY":
        regex = RegExp(/^\d{9,10}$/);
        break;
      default:
        regex = RegExp(/^[a-zA-Z0-9]*$/);
        isValid = false;
        break;
    }

    return (regex.test(value) && isValid) || createError({ path, message });
  });
})

// any separate yup validators, so you don't have to ref customValidators everytime
export { object, string, ref, boolean, array, number, date, mixed }


// you wont need to import yup module separately with this
export { Yup as customValidators }
